import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence, useMotionValue, useAnimation } from 'framer-motion';
import { Github, Mail, ExternalLink, Code, Briefcase, User, MessageSquare, Menu, X, Star, CheckCircle, Clock, Award, Zap, Users, MessageCircle, Send } from 'lucide-react';

// ─── Animation Variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }
  })
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({ opacity: 1, transition: { duration: 0.5, delay: i * 0.07 } })
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

const slideLeft = {
  hidden: { opacity: 0, x: -32 },
  visible: (i = 0) => ({ opacity: 1, x: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } })
};

const slideRight = {
  hidden: { opacity: 0, x: 32 },
  visible: (i = 0) => ({ opacity: 1, x: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } })
};

// ─── Helper: Section reveal wrapper ──────────────────────────────────────────
function Reveal({ children, variants = fadeUp, custom = 0, className = '', once = true }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      custom={custom}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Helper: Stagger container ────────────────────────────────────────────────
function StaggerReveal({ children, className = '', once = true }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ to, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const step = 16;
    const increment = to / (duration / step);
    const timer = setInterval(() => {
      start += increment;
      if (start >= to) { setCount(to); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, step);
    return () => clearInterval(timer);
  }, [inView, to]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── Animated skill bar ───────────────────────────────────────────────────────
function SkillBar({ name, level, color, index }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <div ref={ref}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-gray-300 text-sm font-medium">{name}</span>
        <span className="text-gray-500 text-xs font-mono">{level}%</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={inView ? { width: `${level}%` } : { width: 0 }}
          transition={{ duration: 1, delay: 0.3 + index * 0.09, ease: [0.16, 1, 0.3, 1] }}
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// ─── Floating orb background element ─────────────────────────────────────────
function FloatingOrb({ className }) {
  return (
    <motion.div
      className={className}
      animate={{ y: [0, -20, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Portfolio() {
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [language, setLanguage] = useState('en');

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 80]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const contactDetails = {
    whatsapp: '+79299857728',
    telegram: 'Fredndare',
    email: 'fredjamo68@gmail.com'
  };

  const translations = {
    en: {
      nav: { home: 'Home', about: 'About', services: 'Services', projects: 'Projects', testimonials: 'Testimonials', contactMe: 'Contact', getInTouch: 'Get In Touch' },
      banner: { available: 'Available for new projects', response: '1hr response time', consultation: 'Free consultation' },
      hero: {
        badge: '50+ Projects Delivered · 100% Client Satisfaction',
        title1: 'I Build', title2: 'High-Converting', title3: 'Web Applications',
        subtitle: 'Ruby on Rails & Python Expert | Full-Stack Developer specializing in scalable web applications that drive business growth.',
        startProject: 'Start Your Project', viewPortfolio: 'View Portfolio',
        projectsDone: 'Projects Done', yearsExp: 'Years Experience', satisfaction: 'Satisfaction'
      },
      about: {
        label: 'About Me', title1: 'The Developer', title2: 'Behind the Code',
        bio1: "I'm a full-stack developer running Jamoni Lab — a boutique web development studio focused on building high-quality, scalable web applications. With 5+ years of experience across Ruby on Rails, Python/Django, and React, I've shipped 50+ projects for clients across Europe, Africa, and beyond.",
        bio2: "I believe great software isn't just about clean code — it's about understanding a client's business goals and translating them into digital products that actually drive growth. Whether you need an MVP built fast or a legacy system modernized, I bring both technical depth and business awareness to every project.",
        location: 'Remote · Available Worldwide', skillsTitle: 'Core Tech Stack',
        skills: [
          { name: 'Ruby on Rails', level: 95, color: '#ef4444' },
          { name: 'Python / Django', level: 90, color: '#3b82f6' },
          { name: 'React / Next.js', level: 88, color: '#06b6d4' },
          { name: 'PostgreSQL', level: 85, color: '#8b5cf6' },
          { name: 'REST APIs', level: 92, color: '#10b981' },
          { name: 'Cloud / DevOps', level: 78, color: '#f59e0b' },
        ],
        cta: 'Work With Me'
      },
      services: {
        title1: 'How I Can', title2: 'Help You',
        subtitle: 'Comprehensive development services tailored to your business goals',
        discuss: 'Discuss Your Project',
        rails: { title: 'Ruby on Rails Apps', desc: 'Enterprise-grade web applications built with Ruby on Rails for rapid development and scalability' },
        python: { title: 'Python/Django Solutions', desc: 'Robust backend systems and data-driven applications using Python and Django' },
        react: { title: 'React Frontends', desc: 'Modern, responsive user interfaces with React.js and modern JavaScript' },
        fullstack: { title: 'Full-Stack Development', desc: 'Complete end-to-end solutions combining Rails/Django backends with React frontends' },
        legacy: { title: 'Legacy App Modernization', desc: 'Upgrade outdated Rails or Django apps to modern standards and best practices' },
        consulting: { title: 'Consulting & Support', desc: 'Expert guidance for your technical decisions and ongoing maintenance' }
      },
      projects: {
        title1: 'Success', title2: 'Stories',
        subtitle: 'Real projects, real results for real businesses',
        featured: 'Featured', visitSite: 'Visit Live Site', buildSimilar: 'Build Similar',
        weandare: { desc: 'Professional web platform delivering innovative digital solutions with modern design and seamless user experience', result: 'Live Platform' },
        tizile: { desc: 'Dynamic web application providing cutting-edge solutions with robust functionality and elegant interface', result: 'Production Ready' }
      },
      testimonials: {
        title1: 'What Clients', title2: 'Say', subtitle: "Don't just take my word for it",
        response24: '24hr Response', quality: '100% Quality', onTime: 'On-Time Delivery',
        happyClients: '50+ Happy Clients', startStory: 'Start Your Success Story'
      },
      contact: {
        title: "Let's Connect!", subtitle: 'Choose your preferred way to reach me',
        whatsapp: 'WhatsApp', whatsappDesc: 'Chat instantly',
        telegram: 'Telegram', telegramDesc: 'Message on Telegram',
        email: 'Email', responds: 'Usually responds within 1 hour', responseRate: '100% response rate'
      },
      footer: {
        rights: '© 2024 Jamoni Lab · Professional Web & Software Development',
        tagline: 'Turning Ideas into Profitable Digital Products',
        getInTouch: 'Get In Touch'
      }
    },
    it: {
      nav: { home: 'Home', about: 'Chi Sono', services: 'Servizi', projects: 'Progetti', testimonials: 'Testimonianze', contactMe: 'Contatto', getInTouch: 'Mettiti in Contatto' },
      banner: { available: 'Disponibile per nuovi progetti', response: 'Risposta entro 1 ora', consultation: 'Consulenza gratuita' },
      hero: {
        badge: '50+ Progetti · 100% Soddisfazione',
        title1: 'Creo', title2: 'Applicazioni Web', title3: 'Ad Alto Rendimento',
        subtitle: 'Esperto Ruby on Rails & Python | Sviluppatore Full-Stack specializzato in applicazioni web scalabili.',
        startProject: 'Inizia il Progetto', viewPortfolio: 'Vedi Portfolio',
        projectsDone: 'Progetti', yearsExp: 'Anni Esperienza', satisfaction: 'Soddisfazione'
      },
      about: {
        label: 'Chi Sono', title1: 'Lo Sviluppatore', title2: 'Dietro il Codice',
        bio1: "Sono uno sviluppatore full-stack che gestisce Jamoni Lab — uno studio di sviluppo web specializzato in applicazioni di alta qualità. Con 5+ anni di esperienza in Ruby on Rails, Python/Django e React, ho consegnato 50+ progetti.",
        bio2: "Il software eccellente non riguarda solo il codice pulito — si tratta di capire gli obiettivi aziendali e tradurli in prodotti digitali che guidano la crescita.",
        location: 'Remoto · Disponibile Ovunque', skillsTitle: 'Stack Tecnologico',
        skills: [
          { name: 'Ruby on Rails', level: 95, color: '#ef4444' },
          { name: 'Python / Django', level: 90, color: '#3b82f6' },
          { name: 'React / Next.js', level: 88, color: '#06b6d4' },
          { name: 'PostgreSQL', level: 85, color: '#8b5cf6' },
          { name: 'REST APIs', level: 92, color: '#10b981' },
          { name: 'Cloud / DevOps', level: 78, color: '#f59e0b' },
        ],
        cta: 'Lavora Con Me'
      },
      services: {
        title1: 'Come Posso', title2: 'Aiutarti',
        subtitle: 'Servizi di sviluppo completi su misura per i tuoi obiettivi',
        discuss: 'Discuti il Progetto',
        rails: { title: 'App Ruby on Rails', desc: 'Applicazioni web enterprise costruite con Ruby on Rails' },
        python: { title: 'Soluzioni Python/Django', desc: 'Sistemi backend robusti con Python e Django' },
        react: { title: 'Frontend React', desc: 'Interfacce moderne e responsive con React.js' },
        fullstack: { title: 'Full-Stack', desc: 'Soluzioni complete Rails/Django + React' },
        legacy: { title: 'Modernizzazione', desc: 'Aggiorna app obsolete agli standard moderni' },
        consulting: { title: 'Consulenza', desc: 'Guida esperta per le tue decisioni tecniche' }
      },
      projects: {
        title1: 'Storie di', title2: 'Successo',
        subtitle: 'Progetti reali, risultati reali',
        featured: 'In Evidenza', visitSite: 'Visita Sito', buildSimilar: 'Progetto Simile',
        weandare: { desc: 'Piattaforma web professionale con design moderno', result: 'Live' },
        tizile: { desc: 'Applicazione web dinamica con funzionalità robuste', result: 'Produzione' }
      },
      testimonials: {
        title1: 'Cosa Dicono', title2: 'i Clienti', subtitle: 'Non credermi sulla parola',
        response24: 'Risposta 24h', quality: 'Qualità 100%', onTime: 'Consegna Puntuale',
        happyClients: '50+ Clienti', startStory: 'Inizia la Storia'
      },
      contact: {
        title: 'Mettiamoci in Contatto!', subtitle: 'Scegli il tuo modo preferito',
        whatsapp: 'WhatsApp', whatsappDesc: 'Chatta istantaneamente',
        telegram: 'Telegram', telegramDesc: 'Messaggia su Telegram',
        email: 'Email', responds: 'Risponde entro 1 ora', responseRate: '100% tasso risposta'
      },
      footer: {
        rights: '© 2024 Jamoni Lab · Sviluppo Web Professionale',
        tagline: 'Trasformare Idee in Prodotti Digitali',
        getInTouch: 'Mettiti in Contatto'
      }
    }
  };

  const t = translations[language];

  const testimonials = [
    { name: "Sarah Johnson", company: "TechStart Inc", text: "Delivered our MVP in record time. The code quality was exceptional and the communication was flawless.", rating: 5 },
    { name: "Michael Chen", company: "GrowthLabs", text: "Transformed our vision into reality. Highly professional and always goes the extra mile.", rating: 5 },
    { name: "Emma Rodriguez", company: "Digital Ventures", text: "Best developer we've worked with. Fast, reliable, and truly understands business needs.", rating: 5 }
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    const interval = setInterval(() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length), 5000);
    return () => { window.removeEventListener('scroll', handleScroll); clearInterval(interval); };
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const openContactModal = () => { setShowContactModal(true); setMenuOpen(false); };

  const handleContactClick = (method) => {
    const urls = {
      whatsapp: `https://wa.me/${contactDetails.whatsapp}?text=Hi! I'm interested in your services.`,
      telegram: `https://t.me/${contactDetails.telegram}`,
      email: `mailto:${contactDetails.email}?subject=Project Inquiry`
    };
    if (urls[method]) window.open(urls[method], '_blank');
  };

  const navItems = ['home', 'about', 'services', 'projects', 'testimonials'];

  // ─── Contact Modal ──────────────────────────────────────────────────────────
  const ContactModal = () => (
    <AnimatePresence>
      {showContactModal && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={() => setShowContactModal(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          >
            <motion.button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <X className="w-4 h-4 text-gray-600" />
            </motion.button>
            <div className="bg-gray-900 p-8 text-white text-center">
              <motion.div
                className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4"
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.1 }}
              >
                <MessageSquare className="w-8 h-8 text-white" />
              </motion.div>
              <h3 className="text-2xl font-bold mb-1">{t.contact.title}</h3>
              <p className="text-gray-400 text-sm">{t.contact.subtitle}</p>
            </div>
            <div className="p-6 space-y-3">
              {[
                { key: 'whatsapp', icon: <MessageCircle className="w-5 h-5 text-white" />, bg: 'bg-emerald-500', label: t.contact.whatsapp, desc: t.contact.whatsappDesc, border: 'border-emerald-100 hover:border-emerald-300' },
                { key: 'telegram', icon: <Send className="w-5 h-5 text-white" />, bg: 'bg-blue-500', label: t.contact.telegram, desc: t.contact.telegramDesc, border: 'border-blue-100 hover:border-blue-300' },
                { key: 'email', icon: <Mail className="w-5 h-5 text-white" />, bg: 'bg-gray-800', label: t.contact.email, desc: contactDetails.email, border: 'border-gray-100 hover:border-gray-300' },
              ].map(({ key, icon, bg, label, desc, border }, i) => (
                <motion.button
                  key={key}
                  onClick={() => handleContactClick(key)}
                  className={`w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-white rounded-xl border-2 ${border} transition-colors group`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ scale: 1.02, x: 3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>{icon}</div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-800">{label}</div>
                    <div className="text-xs text-gray-500 truncate">{desc}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </motion.button>
              ))}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-center gap-6 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-emerald-500" />{t.contact.responds}</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" />{t.contact.responseRate}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans" style={{ fontFamily: "'DM Sans', 'Outfit', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        .noise-bg::before {
          content: '';
          position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 0;
        }
        .dot-grid {
          background-image: radial-gradient(circle, #d1d5db 1px, transparent 1px);
          background-size: 24px 24px;
        }
      `}</style>

      {/* Top announcement bar */}
      <motion.div
        className="bg-gray-900 text-gray-300 py-2.5 text-center text-xs font-medium tracking-wide"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="flex items-center justify-center gap-6 flex-wrap px-4">
          <motion.span className="flex items-center gap-1.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <motion.span
              className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block"
              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            {t.banner.available}
          </motion.span>
          <motion.span className="hidden sm:flex items-center gap-1.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <Zap className="w-3 h-3 text-emerald-400" />{t.banner.response}
          </motion.span>
          <motion.span className="hidden sm:flex items-center gap-1.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <CheckCircle className="w-3 h-3 text-emerald-400" />{t.banner.consultation}
          </motion.span>
        </div>
      </motion.div>

      {/* Navbar */}
      <motion.nav
        className={`sticky top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/98 shadow-sm border-b border-gray-100' : 'bg-white/90'} backdrop-blur-xl`}
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <motion.div
              className="flex items-center gap-2.5 cursor-pointer"
              onClick={() => scrollToSection('home')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <motion.div
                className="w-9 h-9 bg-gray-900 rounded-xl flex items-center justify-center"
                whileHover={{ rotate: [0, -10, 10, 0], backgroundColor: '#10b981' }}
                transition={{ duration: 0.4 }}
              >
                <Code className="text-emerald-400" style={{ width: '18px', height: '18px' }} />
              </motion.div>
              <div>
                <div className="text-base font-bold text-gray-900 leading-none">Jamoni Lab</div>
                <div className="text-[10px] text-gray-400 font-medium tracking-wider uppercase">Web & Software</div>
              </div>
            </motion.div>

            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((s, i) => (
                <motion.button
                  key={s}
                  onClick={() => scrollToSection(s)}
                  className={`px-4 py-2 text-sm font-medium capitalize rounded-lg transition-colors ${activeSection === s ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.nav[s]}
                </motion.button>
              ))}
              <motion.button
                onClick={openContactModal}
                className="ml-3 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl flex items-center gap-2"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.05, backgroundColor: '#059669' }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageSquare className="w-3.5 h-3.5" />{t.nav.contactMe}
              </motion.button>
              <motion.button
                onClick={() => setLanguage(l => l === 'en' ? 'it' : 'en')}
                className="ml-2 w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-sm"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9, rotate: 20 }}
              >
                {language === 'en' ? '🇮🇹' : '🇬🇧'}
              </motion.button>
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <motion.button onClick={openContactModal} className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5" whileTap={{ scale: 0.95 }}>
                <MessageSquare className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.nav.contactMe}</span>
              </motion.button>
              <motion.button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-xl hover:bg-gray-100"
                whileTap={{ scale: 0.9 }}
              >
                <AnimatePresence mode="wait">
                  {menuOpen
                    ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}><X className="w-5 h-5 text-gray-700" /></motion.div>
                    : <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}><Menu className="w-5 h-5 text-gray-700" /></motion.div>
                  }
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="lg:hidden bg-white border-t border-gray-100 shadow-lg overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="px-4 py-4 flex flex-col gap-1 max-w-6xl mx-auto">
                {navItems.map((s, i) => (
                  <motion.button
                    key={s}
                    onClick={() => scrollToSection(s)}
                    className={`text-left capitalize font-medium py-3 px-4 rounded-xl text-sm ${activeSection === s ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ x: 4 }}
                  >
                    {t.nav[s]}
                  </motion.button>
                ))}
                <div className="pt-3 border-t border-gray-100 flex gap-2 mt-1">
                  {['en', 'it'].map((lang) => (
                    <motion.button
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${language === lang ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {lang === 'en' ? '🇬🇧 English' : '🇮🇹 Italiano'}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO ── */}
      <section id="home" className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 overflow-hidden dot-grid">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-gray-50 pointer-events-none" />

        {/* Floating background orbs */}
        <FloatingOrb className="absolute top-20 right-16 w-72 h-72 bg-emerald-100/60 rounded-full blur-3xl pointer-events-none" />
        <FloatingOrb className="absolute bottom-32 left-16 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          className="absolute top-1/2 left-1/2 w-96 h-96 bg-emerald-50/80 rounded-full blur-3xl pointer-events-none"
          style={{ x: '-50%', y: '-50%' }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        <motion.div
          className="relative max-w-4xl w-full text-center z-10 py-24"
          style={{ y: heroY, opacity: heroOpacity }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gray-900 text-gray-300 px-4 py-2 rounded-full text-xs font-medium mb-8 tracking-wide"
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ scale: 1.04 }}
          >
            <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            </motion.span>
            {t.hero.badge}
          </motion.div>

          <motion.h1
            className="text-5xl sm:text-6xl md:text-7xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            {t.hero.title1}{' '}
            <motion.span
              className="relative inline-block text-emerald-500"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              {t.hero.title2}
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {t.hero.title3}
            </motion.span>
          </motion.h1>

          <motion.p
            className="text-lg sm:text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3 justify-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.button
              onClick={openContactModal}
              className="px-8 py-4 bg-gray-900 text-white rounded-xl font-semibold flex items-center justify-center gap-2 text-sm"
              whileHover={{ scale: 1.05, backgroundColor: '#059669', y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <MessageSquare className="w-4 h-4" />{t.hero.startProject}
            </motion.button>
            <motion.button
              onClick={() => scrollToSection('projects')}
              className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold border border-gray-200 text-sm"
              whileHover={{ scale: 1.05, borderColor: '#9ca3af', y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              {t.hero.viewPortfolio}
            </motion.button>
          </motion.div>

          <motion.div
            className="grid grid-cols-3 gap-4 max-w-sm mx-auto"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
          >
            {[
              { val: 50, suffix: '+', label: t.hero.projectsDone },
              { val: 5, suffix: '+', label: t.hero.yearsExp },
              { val: 100, suffix: '%', label: t.hero.satisfaction },
            ].map(({ val, suffix, label }, i) => (
              <motion.div
                key={label}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                whileHover={{ y: -4, shadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="text-2xl font-black text-gray-900">
                  <Counter to={val} suffix={suffix} />
                </div>
                <div className="text-xs text-gray-500 font-medium mt-0.5">{label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <span className="text-xs text-gray-400 tracking-wider">SCROLL</span>
          <motion.div
            className="w-5 h-8 rounded-full border-2 border-gray-300 flex items-start justify-center p-1"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              className="w-1 h-2 bg-gray-400 rounded-full"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-24 px-4 sm:px-6 relative bg-gray-900 noise-bg overflow-hidden">
        <FloatingOrb className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
        <FloatingOrb className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <motion.div
              className="inline-flex items-center gap-2 bg-white/10 text-emerald-400 px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase mb-4 border border-white/10"
              whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.15)' }}
            >
              {t.about.label}
            </motion.div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-16 leading-tight">
              {t.about.title1} <span className="text-emerald-400">{t.about.title2}</span>
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <Reveal variants={slideLeft} custom={0}>
                <div className="flex items-start gap-6">
                  <motion.div
                    className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl ring-4 ring-white/10"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <User className="w-10 h-10 text-white" />
                  </motion.div>
                  <div>
                    <div className="text-white font-bold text-xl">Fred — Jamoni Lab</div>
                    <div className="text-gray-400 text-sm mt-1 flex items-center gap-1.5">
                      <motion.span
                        className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block"
                        animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      {t.about.location}
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal variants={slideLeft} custom={1}>
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed text-base">{t.about.bio1}</p>
                  <p className="text-gray-400 leading-relaxed text-sm">{t.about.bio2}</p>
                </div>
              </Reveal>

              <StaggerReveal className="flex flex-wrap gap-2">
                {['Rails 7+', 'Python', 'React', 'PostgreSQL', 'REST APIs', 'Docker', 'AWS', 'Stripe'].map(tag => (
                  <motion.span
                    key={tag}
                    variants={scaleIn}
                    className="px-3 py-1 bg-white/10 text-gray-300 rounded-lg text-xs font-medium border border-white/10 cursor-default"
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.18)', scale: 1.06, y: -2 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  >
                    {tag}
                  </motion.span>
                ))}
              </StaggerReveal>

              <Reveal variants={slideLeft} custom={3}>
                <motion.button
                  onClick={openContactModal}
                  className="px-6 py-3 bg-emerald-500 text-white font-semibold rounded-xl text-sm flex items-center gap-2"
                  whileHover={{ scale: 1.05, backgroundColor: '#34d399', y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  <MessageSquare className="w-4 h-4" />{t.about.cta}
                </motion.button>
              </Reveal>
            </div>

            <Reveal variants={slideRight}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                <div className="text-white font-bold text-sm uppercase tracking-wider mb-8 flex items-center gap-2">
                  <Code className="w-4 h-4 text-emerald-400" />{t.about.skillsTitle}
                </div>
                <div className="space-y-6">
                  {t.about.skills.map((skill, i) => (
                    <SkillBar key={skill.name} {...skill} index={i} />
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                  {[{ val: 50, suffix: '+', label: 'Projects' }, { val: 5, suffix: '+', label: 'Years' }, { val: 20, suffix: '+', label: 'Clients' }].map(({ val, suffix, label }) => (
                    <motion.div key={label} whileHover={{ scale: 1.08 }}>
                      <div className="text-2xl font-black text-white"><Counter to={val} suffix={suffix} /></div>
                      <div className="text-xs text-gray-500 font-medium">{label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase mb-4">Services</div>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              {t.services.title1} <span className="text-emerald-500">{t.services.title2}</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">{t.services.subtitle}</p>
          </Reveal>

          <StaggerReveal className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Code className="w-5 h-5" />, ...t.services.rails, features: ['Rails 7+', 'API Development', 'PostgreSQL'] },
              { icon: <Zap className="w-5 h-5" />, ...t.services.python, features: ['Django REST', 'Data Processing', 'ML Integration'] },
              { icon: <User className="w-5 h-5" />, ...t.services.react, features: ['React 18+', 'Redux/Context', 'Next.js'] },
              { icon: <Users className="w-5 h-5" />, ...t.services.fullstack, features: ['API Design', 'Real-time', 'Cloud Deploy'] },
              { icon: <Briefcase className="w-5 h-5" />, ...t.services.legacy, features: ['Refactoring', 'Performance', 'Security'] },
              { icon: <Award className="w-5 h-5" />, ...t.services.consulting, features: ['Tech Strategy', '24/7 Support', 'Code Reviews'] },
            ].map((service, idx) => (
              <motion.div
                key={idx}
                variants={scaleIn}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 group cursor-default"
                whileHover={{ y: -6, boxShadow: '0 20px 40px rgba(0,0,0,0.08)', borderColor: '#d1fae5' }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <motion.div
                  className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-emerald-400 mb-5"
                  whileHover={{ scale: 1.15, backgroundColor: '#10b981', color: '#ffffff', rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {service.icon}
                </motion.div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">{service.desc}</p>
                <ul className="space-y-1.5">
                  {service.features.map((f, fi) => (
                    <motion.li
                      key={f}
                      className="flex items-center gap-2 text-xs text-gray-600"
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: fi * 0.05 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{f}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </StaggerReveal>

          <Reveal className="mt-12 text-center">
            <motion.button
              onClick={openContactModal}
              className="px-8 py-3.5 bg-gray-900 text-white rounded-xl font-semibold text-sm flex items-center gap-2 mx-auto"
              whileHover={{ scale: 1.05, backgroundColor: '#059669', y: -2 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <MessageSquare className="w-4 h-4" />{t.services.discuss}
            </motion.button>
          </Reveal>
        </div>
      </section>

      {/* ── PROJECTS ── */}
      <section id="projects" className="py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-200 text-gray-600 px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase mb-4">Portfolio</div>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              {t.projects.title1} <span className="text-emerald-500">{t.projects.title2}</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">{t.projects.subtitle}</p>
          </Reveal>

          <StaggerReveal className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Weandare.com', ...t.projects.weandare, tech: ['React', 'Modern UI/UX', 'Responsive'], color: 'bg-indigo-500', link: 'https://weandare.com', featured: true },
              { title: 'Tizile.com', ...t.projects.tizile, tech: ['Full-Stack', 'Scalable', 'Cloud'], color: 'bg-cyan-500', link: 'https://www.tizile.com/', featured: true },
              { title: 'SaaS Platform', desc: 'Multi-tenant subscription platform with Stripe serving 5K+ users', result: '5K+ Users', tech: ['Rails', 'PostgreSQL', 'Sidekiq'], color: 'bg-rose-500', link: '#' },
              { title: 'E-Commerce API', desc: 'RESTful API handling 50K requests/day with 99.9% uptime', result: '50K Req/Day', tech: ['Django', 'Python', 'Redis'], color: 'bg-emerald-500', link: '#' },
              { title: 'Analytics Dashboard', desc: 'Real-time data visualization with React and Rails backend', result: '10K+ Users', tech: ['React', 'Rails API', 'WebSocket'], color: 'bg-blue-500', link: '#' },
              { title: 'Booking System', desc: 'Appointment scheduling with automated reminders and payments', result: '80% Time Saved', tech: ['Rails', 'Stripe', 'Twilio'], color: 'bg-purple-500', link: '#' },
            ].map((project, idx) => (
              <motion.div
                key={idx}
                variants={scaleIn}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden group relative"
                whileHover={{ y: -6, boxShadow: '0 24px 48px rgba(0,0,0,0.10)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                {project.featured && (
                  <motion.div
                    className="absolute top-3 left-3 z-10 bg-gray-900 text-white px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />{t.projects.featured}
                  </motion.div>
                )}
                <div className={`w-full h-36 ${project.color} flex items-center justify-center relative overflow-hidden`}>
                  <motion.div
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"
                    transition={{ duration: 0.3 }}
                  />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  >
                    <Code className="w-12 h-12 text-white/30" />
                  </motion.div>
                  <div className="absolute top-3 right-3 bg-white/95 px-2.5 py-1 rounded-full text-xs font-bold text-gray-800">
                    {project.result}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-gray-900 mb-1.5">{project.title}</h3>
                  <p className="text-gray-500 text-xs mb-4 leading-relaxed">{project.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tech.map((tech) => (
                      <motion.span
                        key={tech}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-medium"
                        whileHover={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                  {project.link !== '#' ? (
                    <motion.a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-emerald-600 font-semibold text-xs"
                      whileHover={{ x: 4, color: '#059669' }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      {t.projects.visitSite} <ExternalLink className="w-3 h-3" />
                    </motion.a>
                  ) : (
                    <motion.button
                      onClick={openContactModal}
                      className="flex items-center gap-1.5 text-gray-500 font-semibold text-xs"
                      whileHover={{ x: 4, color: '#374151' }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      {t.projects.buildSimilar} <ExternalLink className="w-3 h-3" />
                    </motion.button>
                  )}
                </div>
              </motion.div>
            ))}
          </StaggerReveal>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal className="mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase mb-4">Testimonials</div>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-3">
              {t.testimonials.title1} <span className="text-emerald-500">{t.testimonials.title2}</span>
            </h2>
            <p className="text-gray-500">{t.testimonials.subtitle}</p>
          </Reveal>

          <Reveal custom={1}>
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 sm:p-10 mb-8 relative overflow-hidden">
              <div className="flex justify-center mb-5">
                {[...Array(5)].map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0, rotate: -30 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: i * 0.07, type: 'spring', stiffness: 400, damping: 15 }}
                  >
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </motion.span>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, y: 20, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.97 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <p className="text-xl text-gray-700 italic mb-6 leading-relaxed">"{testimonials[currentTestimonial].text}"</p>
                  <div className="font-bold text-gray-900">{testimonials[currentTestimonial].name}</div>
                  <div className="text-emerald-600 text-sm font-medium">{testimonials[currentTestimonial].company}</div>
                </motion.div>
              </AnimatePresence>

              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, i) => (
                  <motion.button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === currentTestimonial ? 'bg-gray-900 w-6' : 'bg-gray-300 w-1.5'}`}
                    whileHover={{ scale: 1.3 }}
                    whileTap={{ scale: 0.9 }}
                  />
                ))}
              </div>
            </div>
          </Reveal>

          <StaggerReveal className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: <Clock className="w-5 h-5" />, label: t.testimonials.response24 },
              { icon: <Award className="w-5 h-5" />, label: t.testimonials.quality },
              { icon: <CheckCircle className="w-5 h-5" />, label: t.testimonials.onTime },
              { icon: <Users className="w-5 h-5" />, label: t.testimonials.happyClients },
            ].map(({ icon, label }) => (
              <motion.div
                key={label}
                variants={scaleIn}
                className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center"
                whileHover={{ y: -4, borderColor: '#a7f3d0', boxShadow: '0 8px 24px rgba(16,185,129,0.1)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="flex justify-center text-emerald-500 mb-2"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                >
                  {icon}
                </motion.div>
                <div className="text-xs font-semibold text-gray-700">{label}</div>
              </motion.div>
            ))}
          </StaggerReveal>

          <Reveal custom={3}>
            <motion.button
              onClick={openContactModal}
              className="px-8 py-3.5 bg-gray-900 text-white rounded-xl font-semibold text-sm flex items-center gap-2 mx-auto"
              whileHover={{ scale: 1.05, backgroundColor: '#059669', y: -2 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <MessageSquare className="w-4 h-4" />{t.testimonials.startStory}
            </motion.button>
          </Reveal>
        </div>
      </section>

      <ContactModal />

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-white/5 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center space-y-5">
          <StaggerReveal className="flex items-center justify-center gap-3 mb-2">
            {[
              { href: `https://wa.me/${contactDetails.whatsapp}`, icon: <MessageCircle className="w-4 h-4" />, hoverBg: 'hover:bg-emerald-500' },
              { href: `https://t.me/${contactDetails.telegram}`, icon: <Send className="w-4 h-4" />, hoverBg: 'hover:bg-blue-500' },
              { href: `mailto:${contactDetails.email}`, icon: <Mail className="w-4 h-4" />, hoverBg: 'hover:bg-purple-500' },
              { href: 'https://github.com/jamo254', icon: <Github className="w-4 h-4" />, hoverBg: 'hover:bg-gray-600' },
            ].map(({ href, icon, hoverBg }, i) => (
              <motion.a
                key={i}
                variants={scaleIn}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white ${hoverBg} transition-colors`}
                whileHover={{ scale: 1.15, y: -3 }}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {icon}
              </motion.a>
            ))}
          </StaggerReveal>
          <Reveal>
            <p className="text-gray-500 text-sm">{t.footer.rights}</p>
            <p className="text-gray-600 text-xs mt-1">{t.footer.tagline}</p>
          </Reveal>
          <Reveal custom={1}>
            <motion.button
              onClick={openContactModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-xl font-semibold text-sm"
              whileHover={{ scale: 1.06, backgroundColor: '#34d399', y: -2 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <MessageSquare className="w-3.5 h-3.5" />{t.footer.getInTouch}
            </motion.button>
          </Reveal>
        </div>
      </footer>
    </div>
  );
} 